import { useState } from 'react';

import { CloseIcon } from '@braskai/icons';
import { Button, Modal, Typography } from '@braskai/ui-kit';

import { useAfterPurchaseModalStore } from '@/entities/afterPurchase';
import { useCurrentUser } from '@/entities/user/currentUser';
import { useReliableUserRefresh } from '@/entities/user/currentUser/model/useReliableUserRefresh';

import { API } from '@/shared/api';
import { FeaturedResumeIcon } from '@/shared/assets/icons/FeaturedResumeIcon';
import { ONE_UNIT_CENTS_COUNT } from '@/shared/constants/common';
import { showErrorAlert } from '@/shared/lib/showErrorAlert';
import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';

import { useCancelPauseModalStore } from '../model/useCancelPauseModalStore';
import {
  SActions,
  SCloseButton,
  SContent,
  SHeader,
  SHeaderTop,
  SModalContainer,
  SPlanSection,
} from './styled';

const formatDate = (date: Date | string) => {
  const dateObj = new Date(date);
  return dateObj.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

export const CancelPauseModal = () => {
  const isOpen = useCancelPauseModalStore((state) => state.isOpen);
  const close = useCancelPauseModalStore((state) => state.close);
  const onSuccess = useCancelPauseModalStore((state) => state.onSuccess);

  const { data: user } = useCurrentUser();
  const { reliableRefresh } = useReliableUserRefresh();
  const { openSuccess } = useAfterPurchaseModalStore();

  const [isLoading, setIsLoading] = useState(false);

  const userPriceData = user?.subscription?.plan;
  const currentPlanPrice = userPriceData?.amount ? userPriceData.amount / ONE_UNIT_CENTS_COUNT : 0;
  const pausedUntil = user?.subscription?.payments_collection_paused_until;
  const currentPeriodEnd = user?.subscription?.current_period_end;
  const intervalLabel = userPriceData?.interval === 'year' ? 'year' : 'month';

  const handleResume = async () => {
    MixpanelEvents.renewSubscriptionPauseClick();

    try {
      setIsLoading(true);

      const response = await API.resumeSubscription();

      if (response.ok) {
        close();
        void reliableRefresh();
        onSuccess?.();
        openSuccess('Subscription resumed.\nYou will be billed on your next subscription renewal date.');
        MixpanelEvents.renewSubscriptionPauseDone();
      }
    } catch (e) {
      showErrorAlert(e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    close();
  };

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <SModalContainer>
        <SHeader>
          <SHeaderTop>
            <FeaturedResumeIcon height={56} width={56} />
            <SCloseButton onClick={handleClose}>
              <CloseIcon height={24} width={24} />
            </SCloseButton>
          </SHeaderTop>
        </SHeader>

        <SContent>
          <SPlanSection>
            <Typography color="grey.900" variant="body-1-md">
              Your subscription is paused
            </Typography>
            <Typography color="grey.700" variant="body-2">
              It's scheduled to reactivate on {pausedUntil ? formatDate(pausedUntil) : 'your next billing date'}
              . Your account data is being kept safe for when your subscription resumes.
            </Typography>
          </SPlanSection>

          <SPlanSection>
            <Typography color="primary.600" variant="body-1-md">
              Want to resume?
            </Typography>
            <Typography color="grey.700" variant="body-2">
              You will be charged at your original rate ${currentPlanPrice}/{intervalLabel} on{' '}
              {currentPeriodEnd ? formatDate(currentPeriodEnd) : 'your next billing date'}.
            </Typography>
          </SPlanSection>
        </SContent>

        <SActions>
          <Button disabled={isLoading} isLoading={isLoading} size="lg" fullWidth onClick={handleResume}>
            Resume subscription now
          </Button>
        </SActions>
      </SModalContainer>
    </Modal>
  );
};
